import * as React from 'react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Chip, TextField, MenuItem, Select, InputLabel, FormControl, FormHelperText, IconButton, Typography, useTheme, Autocomplete } from '@mui/material';
import { Add, CheckCircleOutlined, ChevronRight, Close, KeyboardArrowDown, SearchOutlined } from '@mui/icons-material';
import { Control, Controller, FieldValues, Noop, RefCallBack, UseFormSetValue } from 'react-hook-form';
import { tokens } from '../../theme';

type DropdownOption = { value: string | number; label: string; meta?: string; data?: any };

export type DropdownFieldProps = {
  options: DropdownOption[];
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium";
  label?: string;
  variant?: "filled" | "outlined" | "standard";
  disableSearch?: boolean;
  name?: string;
  onChangeSelect?: (e: any, value: any) => any;
  control?: Control<any, any>;
  rules?: object;
  disabled?: boolean;
  showErrorMinLength?: number;
  setValues?: UseFormSetValue<FieldValues>;
  enableBlurHandler?: boolean;
  enableKeyDownHandler?: boolean;
  sortOptions?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholderText?: string | "";
  withoutSysData?: boolean;
  comboStyle?: boolean;
  onAddManually?: (typedValue?: string) => void;
  addManuallyLabel?: string;
  onUseSystem?: () => void;
  enableAddressSuggestions?: boolean;
  fetchFunction?: (query: string) => Promise<any>;
  transformResponse?: (response: any) => DropdownOption[];
  zIndex?: number;
};

const CustomDropdownField: React.FC<DropdownFieldProps> = ({
  options,
  size = "medium",
  color,
  label,
  variant = "outlined",
  disableSearch = false,
  name,
  control,
  onChangeSelect,
  rules,
  disabled = false,
  showErrorMinLength,
  setValues,
  enableBlurHandler = false,
  enableKeyDownHandler = false,
  sortOptions = true,
  onBlur,
  placeholderText,
  withoutSysData = false,
  comboStyle = false,
  onAddManually,
  addManuallyLabel,
  onUseSystem,
  enableAddressSuggestions = false,
  fetchFunction,
  transformResponse,
  zIndex,
  ...props
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const minHeight = size === "small" ? "35px" : "53px";

  const [comboOpen, setComboOpen] = useState(false);
  const [comboQ, setComboQ] = useState("");
  const comboRef = useRef<HTMLDivElement>(null);
  const [suggestionInputValue, setSuggestionInputValue] = useState("");
  const [suggestionOptions, setSuggestionOptions] = useState<DropdownOption[]>([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  useEffect(() => {
    if (!comboStyle) return;
    const handler = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setComboOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [comboStyle]);

  useEffect(() => {
    if (!enableAddressSuggestions || !fetchFunction || !transformResponse) return;

    const query = (comboStyle ? comboQ : suggestionInputValue).trim();
    if (query.length < 2) {
      setSuggestionOptions([]);
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setSuggestionLoading(true);
      try {
        const response = await fetchFunction(query);
        if (!active) return;
        setSuggestionOptions(transformResponse(response));
      } catch (error) {
        if (!active) return;
        console.error("Dropdown address suggestion fetch error:", error);
        setSuggestionOptions([]);
      } finally {
        if (active) setSuggestionLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [enableAddressSuggestions, fetchFunction, transformResponse, suggestionInputValue, comboStyle, comboQ]);

  /* ── derived tokens for comboStyle (same palette as useModalStyles) ── */
  const ct = {
    inputBg:     isDark ? "rgba(255,255,255,.04)" : "#f8fafc",
    inputBorder: isDark ? "rgba(255,255,255,.10)" : "#cbd5e1",
    accent:      "#14b8a6",
    textPrimary: isDark ? "#e2e8f0" : "#141720",
    textSec:     isDark ? "#64748b" : "#64748b",
    dropBg:      isDark ? "#0c1624" : "#ffffff",
    cardBg:      isDark ? "#1c2030" : "#f1f5f9",
    border:      isDark ? "rgba(255,255,255,.07)" : "#e2e8f0",
    inputBorderFocus: "rgba(20,184,166,.5)",
  };

  /* ── sorted options ── */
  const sortedOptions = sortOptions
    ? [...options].sort((a, b) => a.label.localeCompare(b.label))
    : options;

  /* ── comboStyle filtered list (component-level to satisfy hooks rules) ── */
  const comboFiltered = useMemo(() => {
    const t = comboQ.trim().toLowerCase();
    if (!t) return sortedOptions;
    return sortedOptions.filter(o =>
      o.label.toLowerCase().includes(t) || (o.meta || "").toLowerCase().includes(t)
    );
  }, [comboQ, sortedOptions]);

  const comboExactExists = useMemo(
    () => sortedOptions.some(o => o.label.toLowerCase() === comboQ.trim().toLowerCase()),
    [comboQ, sortedOptions]
  );

  const dropdownScrollbarSx = {
    scrollbarWidth: "thin",
    scrollbarColor: isDark ? "rgba(255,255,255,0.22) transparent" : "#94A3B8 transparent",
    "&::-webkit-scrollbar": { width: "6px", height: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,0.22)" : "#94A3B8", borderRadius: 4 },
  };

  const isRequired = () => rules && typeof rules === "object" && "required" in rules && label;

  const getLabel = () => {
    if (isRequired()) {
      return <span>{label} <span style={{ color: colors.success[200], fontSize: "18px" }}>*</span></span>;
    }
    return label;
  };

  const getFieldValue = (value: any) => {
    const found = sortedOptions.find(o => o.value.toString() === value?.toString());
    return found ? found.value?.toString() : null;
  };

  const handleSelectChange = (event: any, field: any, option: string | null) => {
    const match = sortedOptions.find(o => o.label === option);
    const val = match ? match.value : "";
    field.onChange(val);
    if (onChangeSelect) onChangeSelect(match, match?.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent, value: string) => {
    if (enableKeyDownHandler && event.key === "Enter") {
      const first = sortedOptions.filter(o => o.label.toLowerCase().includes(value.toLowerCase()))[0];
      if (first) {
        setValues && setValues(name || "", first.value);
        if (onChangeSelect) onChangeSelect(null, first.value);
      }
    }
  };

  const handleBlurEvent = (event: any) => {
    if (enableBlurHandler) {
      const first = sortedOptions.filter(o => o.label.toLowerCase().includes(event.target.value.toLowerCase()))[0];
      if (first) {
        setValues && setValues(name || "", first.value);
        if (onChangeSelect) onChangeSelect(null, first.value);
      }
    }
    if (onBlur) onBlur(event);
  };
  
  if (withoutSysData) {
    return (
      <Controller
        name={name || ""}
        control={control}
        rules={rules ?? {}}
        render={({ field, fieldState }) => {
          const manualEndAdornment = onUseSystem ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <Chip
                label="New entry"
                size="small"
                sx={{ backgroundColor: "rgba(139,92,246,0.15)", color: "#a78bfa", border: "1px solid rgba(139,92,246,0.25)", fontWeight: 600, fontSize: "0.68rem", height: 22, "& .MuiChip-label": { px: 1 } }}
              />
              <IconButton size="small" onClick={onUseSystem} sx={{ p: 0.3, color: ct.textSec, "&:hover": { color: ct.accent } }}>
                <Close sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Box>
          ) : undefined;

          const manualFieldSx = {
            minHeight,
            "& .MuiInputBase-root": { minHeight },
            "& label": { color: isDark ? "" : colors.primary[100] },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: isDark ? "" : colors.primary[100] },
            },
          };

          if (enableAddressSuggestions) {
            return (
              <Autocomplete
                freeSolo
                disableClearable={!!onUseSystem}
                options={suggestionOptions}
                loading={suggestionLoading}
                inputValue={field.value == null ? "" : String(field.value)}
                disabled={disabled}
                onInputChange={(_, newInputValue) => {
                  field.onChange(newInputValue);
                  setSuggestionInputValue(newInputValue);
                }}
                onChange={(_, selected) => {
                  const selectedOption = typeof selected === "string" ? null : selected;
                  const selectedValue = typeof selected === "string" ? selected : selectedOption?.label ?? "";
                  field.onChange(selectedValue);
                  setSuggestionInputValue(selectedValue);
                  if (onChangeSelect) onChangeSelect(selectedOption, selectedValue);
                }}
                getOptionLabel={(option) => typeof option === "string" ? option : option.label}
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.label === value.label
                }
                componentsProps={{
                  paper: {
                    sx: {
                      overflow: "hidden",
                      backgroundColor: ct.dropBg,
                      border: `1px solid ${ct.inputBorder}`,
                      borderRadius: "12px",
                      boxShadow: "0 20px 40px -10px rgba(0,0,0,.6)",
                    },
                  },
                }}
                ListboxProps={{
                  sx: {
                    maxHeight: 260, overflowY: "auto", p: 0.7, ...dropdownScrollbarSx,
                    "& .MuiAutocomplete-option": { borderRadius: "8px" },
                    "& .MuiAutocomplete-option.Mui-focused": { borderRadius: "8px" },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={size}
                    variant={variant}
                    placeholder={placeholderText || ""}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? null}
                    color={color || (isDark ? "secondary" : "primary")}
                    onBlur={(event) => {
                      field.onBlur();
                      if (onBlur) onBlur(event);
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {manualEndAdornment}
                        </>
                      ),
                    }}
                    sx={manualFieldSx}
                  />
                )}
              />
            );
          }

          return (
            <TextField
              {...field}
              size={size}
              variant={variant}
              placeholder={placeholderText || ""}
              disabled={disabled}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? null}
              color={color || (isDark ? "secondary" : "primary")}
              InputProps={manualEndAdornment ? { endAdornment: manualEndAdornment } : undefined}
              sx={manualFieldSx}
            />
          );
        }}
      />
    );
  }

  if (comboStyle) {
    return (
      <Controller
        name={name || ""}
        control={control}
        rules={rules ?? {}}
        render={({ field, fieldState }) => {
          const selectedOption = sortedOptions.find(o => String(o.value) === String(field.value));

          const pick = (o: typeof sortedOptions[0]) => {
            field.onChange(o.value);
            if (onChangeSelect) onChangeSelect(o, o.value);
            setComboOpen(false);
            setComboQ("");
          };

          const clear = () => {
            field.onChange("");
            if (onChangeSelect) onChangeSelect(null, "");
            setComboQ("");
          };

          const hasAddressSuggestions = enableAddressSuggestions && suggestionOptions.length > 0;
          const hasComboResults = comboFiltered.length > 0 || hasAddressSuggestions;
          const pickAddressSuggestion = (option: DropdownOption) => {
            field.onChange("");
            if (onAddManually) onAddManually(option.label);
            if (onChangeSelect) onChangeSelect(option, option.label);
            setComboOpen(false);
            setComboQ("");
          };

          /* selected state */
          if (selectedOption) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.5, py: 0.8, borderRadius: "10px", backgroundColor: ct.inputBg, border: `1px solid ${ct.accent}99`, boxShadow: `0 0 0 3px ${ct.accent}18`, cursor: "default" }}>
                <Box sx={{ width: 24, height: 24, borderRadius: "6px", backgroundColor: `${ct.accent}18`, color: ct.accent, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <CheckCircleOutlined sx={{ fontSize: "0.95rem" }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: ct.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedOption.label}</Typography>
                  {selectedOption.meta && <Typography sx={{ fontSize: "0.72rem", color: ct.textSec, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedOption.meta}</Typography>}
                </Box>
                <Chip label="System" size="small" sx={{ backgroundColor: `${ct.accent}18`, color: ct.accent, border: `1px solid ${ct.accent}33`, fontWeight: 700, fontSize: "0.65rem", height: 20 }} />
                {!disabled && (
                  <IconButton size="small" onClick={clear} sx={{ color: ct.textSec }}>
                    <Close sx={{ fontSize: "0.9rem" }} />
                  </IconButton>
                )}
                {fieldState.error && (
                  <Typography sx={{ fontSize: "0.72rem", color: "error.main", mt: 0.4, display: "block" }}>{fieldState.error.message}</Typography>
                )}
              </Box>
            );
          }

          /* search / empty state */
          return (
            <Box ref={comboRef} sx={{ position: "relative" }}>
              {/* ── search input ── */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, borderRadius: "10px", backgroundColor: ct.inputBg, border: `1px solid ${comboOpen ? ct.accent : (fieldState.error ? "#ef4444" : ct.inputBorder)}`, boxShadow: comboOpen ? `0 0 0 3px ${ct.accent}18` : "none", transition: "all .15s" }}>
                <SearchOutlined sx={{ fontSize: "1rem", color: ct.textSec, flexShrink: 0 }} />
                <input
                  value={comboQ}
                  onFocus={() => setComboOpen(true)}
                  onChange={e => { setComboQ(e.target.value); setComboOpen(true); }}
                  placeholder={placeholderText || "Search…"}
                  disabled={disabled}
                  style={{ flex: 1, background: "transparent", border: 0, fontSize: "0.88rem", color: "inherit", padding: "10px 0", outline: "none", cursor: disabled ? "not-allowed" : "text", opacity: disabled ? 0.5 : 1 }}
                />
                <KeyboardArrowDown onClick={() => setComboOpen(o => !o)} sx={{ fontSize: "1rem", color: ct.textSec, transition: "transform .2s", transform: comboOpen ? "rotate(180deg)" : "none", flexShrink: 0, cursor: "pointer" }} />
              </Box>
              {fieldState.error && (
                <Typography sx={{ fontSize: "0.72rem", color: "error.main", mt: 0.4 }}>{fieldState.error.message}</Typography>
              )}

              {/* ── dropdown popup (list only, no footer) ── */}
              {comboOpen && (
                <Box sx={{
                  position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0, zIndex: 1400,
                  backgroundColor: ct.dropBg, border: `1px solid ${ct.inputBorder}`,
                  borderRadius: "12px", boxShadow: "0 20px 40px -10px rgba(0,0,0,.8)", overflow: "hidden",
                  "@keyframes comboIn": { from: { opacity: 0, transform: "translateY(-6px) scale(.98)" }, to: { opacity: 1, transform: "none" } },
                  animation: "comboIn .15s ease both",
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, px: 1.5, py: 0.9, borderBottom: `1px solid ${ct.border}` }}>
                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: ct.textSec, letterSpacing: ".06em", textTransform: "uppercase" }}>
                      {comboFiltered.length} {comboFiltered.length === 1 ? "match" : "matches"} in system
                      {enableAddressSuggestions && hasAddressSuggestions ? ` · ${suggestionOptions.length} address ${suggestionOptions.length === 1 ? "suggestion" : "suggestions"}` : ""}
                    </Typography>
                    {comboQ.trim() && <Typography sx={{ ml: "auto", fontSize: "0.72rem", color: ct.textSec }}>"{comboQ.trim()}"</Typography>}
                  </Box>
                  <Box sx={{ maxHeight: 220, overflowY: "auto", p: 0.7, ...dropdownScrollbarSx }}>
                    {hasComboResults ? (
                      <>
                        {comboFiltered.map(o => (
                          <Box key={o.value} onClick={() => pick(o)}
                            sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: 0.9, borderRadius: "8px", cursor: "pointer", "&:hover": { backgroundColor: ct.cardBg } }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: ct.cardBg, color: ct.textSec, display: "grid", placeItems: "center", flexShrink: 0, fontSize: "0.8rem", fontWeight: 700 }}>
                              {o.label.substring(0, 1).toUpperCase()}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: ct.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</Typography>
                              {o.meta && <Typography sx={{ fontSize: "0.72rem", color: ct.textSec, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.meta}</Typography>}
                            </Box>
                          </Box>
                        ))}
                        {hasAddressSuggestions && comboFiltered.length > 0 && (
                          <Typography sx={{ px: 1.2, pt: 1, pb: 0.4, fontSize: "0.68rem", fontWeight: 700, color: ct.textSec, letterSpacing: ".06em", textTransform: "uppercase" }}>
                            Address Suggestions
                          </Typography>
                        )}
                        {suggestionOptions.map(o => (
                          <Box key={`address-${o.value}`} onClick={() => pickAddressSuggestion(o)}
                            sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: 0.9, borderRadius: "8px", cursor: "pointer", "&:hover": { backgroundColor: ct.cardBg } }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: "rgba(139,92,246,.16)", color: "#c4b5fd", display: "grid", placeItems: "center", flexShrink: 0, fontSize: "0.8rem", fontWeight: 700 }}>
                              <Add sx={{ fontSize: "1rem" }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: ct.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</Typography>
                              <Typography sx={{ fontSize: "0.72rem", color: ct.textSec, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Use as manual address</Typography>
                            </Box>
                          </Box>
                        ))}
                      </>
                    ) : suggestionLoading ? (
                      <Typography sx={{ py: 2, textAlign: "center", fontSize: "0.78rem", color: ct.textSec }}>
                        Searching addresses...
                      </Typography>
                    ) : (
                      <Typography sx={{ py: 2, textAlign: "center", fontSize: "0.78rem", color: ct.textSec }}>
                        No matching options found
                      </Typography>
                    )}
                  </Box>

                  {/* footer — only when onAddManually is provided */}
                  {onAddManually && (
                    <Box
                      onClick={() => {
                        onAddManually(comboQ.trim());
                        setComboOpen(false);
                        setComboQ("");
                      }}
                      sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.5, py: 1.1, borderTop: `1px solid ${ct.inputBorder}`, backgroundColor: "#18222f", cursor: "pointer", "&:hover": { backgroundColor: "rgba(38, 53, 73, 0.18)" } }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: "8px", backgroundColor: "rgba(139,92,246,.2)", color: "#c4b5fd", display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <Add sx={{ fontSize: "1rem" }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: ct.textPrimary }}>
                          {comboQ.trim() ? `Add "${comboQ.trim()}" manually` : "Not in the platform? Add manually"}
                        </Typography>
                        {addManuallyLabel && (
                          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>{addManuallyLabel} - new record</Typography>
                        )}
                      </Box>
                      <ChevronRight sx={{ fontSize: "0.9rem", color: "#c4b5fd", flexShrink: 0 }} />
                    </Box>
                  )}
                </Box>
              )}

            </Box>
          );
        }}
      />
    );
  }

  if (disableSearch) {
    return (
      <Controller
        name={name || ""}
        control={control}
        rules={rules ?? {}}
        render={({ field: { ref, ...field }, fieldState }) => (
          <>
            <FormControl fullWidth error={!!fieldState.error} disabled={disabled}>
              <InputLabel color={color || (isDark ? "secondary" : "primary")} shrink={Boolean(field.value)}>{label}</InputLabel>
              <Select
                fullWidth {...field}
                label={getLabel() || ""}
                color={color || (isDark ? "secondary" : "primary")}
                style={{ maxHeight: 200 }}
                name={name}
                value={getFieldValue(field.value)}
                onChange={e => { field.onChange(e.target.value); onChangeSelect && onChangeSelect(e, e.target.value); }}
                onBlur={e => { field.onBlur(); handleBlurEvent(e); }}
                onKeyDown={e => handleKeyDown(e, field.value)}
                inputProps={{ shrink: Boolean(field.value) }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 260, overflowY: "auto", ...dropdownScrollbarSx } } }}
                error={!!fieldState.error}
                disabled={disabled}
                {...props}
              >
                {sortedOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {fieldState.error ? ((showErrorMinLength && showErrorMinLength <= (fieldState.error.message?.length ?? 0)) ? null : fieldState.error.message) : null}
              </FormHelperText>
            </FormControl>
          </>
        )}
      />
    );
  }

  return (
    <Controller
      name={name || ""}
      control={control}
      rules={rules ?? {}}
      render={({ field: { ref, ...field }, fieldState }) => (
        <>
          <Autocomplete
            options={sortedOptions.map(o => o.label)}
            value={sortedOptions.find(o => String(o.value) === String(field.value))?.label || ""}
            onChange={(event, newValue) => handleSelectChange(event, field, newValue)}
            disabled={disabled}
            componentsProps={{
              paper: {
                sx: {
                  overflow: "hidden",
                  backgroundColor: ct.dropBg,
                  border: `1px solid ${ct.inputBorder}`,
                  borderRadius: "12px",
                  boxShadow: "0 20px 40px -10px rgba(0,0,0,.6)",
                },
              },
              popper: {
                sx: zIndex !== undefined ? { zIndex } : undefined,
                modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
              },
            }}
            ListboxProps={{
              sx: {
                maxHeight: 260, overflowY: "auto", p: 0.7, ...dropdownScrollbarSx,
                "& .MuiAutocomplete-option": { borderRadius: "8px" },
                "& .MuiAutocomplete-option.Mui-focused": { borderRadius: "8px" },
              },
            }}
            freeSolo={false}
            autoSelect={false}
            clearOnBlur={true}
            renderInput={params => (
              <TextField
                {...params}
                label={getLabel() || ""}
                placeholder={placeholderText || ""}
                onBlur={handleBlurEvent}
                variant={variant}
                size={size}
                name={name}
                color={color || (isDark ? "secondary" : "primary")}
                error={!!fieldState.error}
                helperText={fieldState.error ? ((showErrorMinLength && showErrorMinLength <= (fieldState.error.message?.length ?? 0)) ? null : fieldState.error.message) : null}
                onKeyDown={e => handleKeyDown(e, field.value)}
                sx={{
                  minHeight,
                  "& .MuiInputBase-root": { minHeight },
                  "& .MuiChip-root": { margin: "0px 2px" },
                  "& .MuiChip-root.Mui-disabled": { margin: "0px 2px", color: isDark ? "" : "black" },
                  "& label": { color: isDark ? "" : colors.primary[100] },
                  "& label.Mui-disabled": { color: isDark ? "" : colors.grey[400] },
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: isDark ? "" : colors.primary[100] } },
                  "& .MuiInputBase-root.Mui-disabled": { "& fieldset": { borderColor: isDark ? "" : colors.grey[500] } },
                }}
              />
            )}
          />
        </>
      )}
    />
  );
};

export default CustomDropdownField;
